import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import supabase from "../supabase";
import { formatDeadline } from "../lib/formatDeadline";

export default function RequestDetailsPage({ organization, profile }) {
  const { companyId, requestId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Security check
    if (organization?.id !== "821b771e-d2ea-4b98-8570-c59e4e14bed5") return;

    const fetchRequestDetails = async () => {
      try {
        const { data, error } = await supabase
          .from("Requests")
          .select(
            `
            id,
            article_id,
            title,
            status,
            created_at,
            brief_data,
            Organizations ( name ),
            company_id,
            projects ( title ),
            opened
          `
          )
          .eq("id", requestId)
          .single();

        if (error) throw error;
        setRequest(data);

        // mark as opened so it drops off the unread-requests badge in the nav
        if (data && !data.opened) {
          const { error: openedError } = await supabase
            .from("Requests")
            .update({ opened: true })
            .eq("id", requestId);
          if (openedError) {
            console.error("Failed to mark request as opened:", openedError);
          }
        }
      } catch (err) {
        console.error("Error fetching request details:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRequestDetails();
  }, [requestId, organization]);

  if (loading) return <div style={styles.page}>Loading details...</div>;
  if (!request) return <div style={styles.page}>Request not found.</div>;

  const data = request.brief_data || {};
  const assets = data.uploaded_assets || [];

  const canSee = (buttonName) => {
    const role = (profile?.role || "").toLowerCase();
    switch (buttonName) {
      case "Ready for Review":
        return ["admin", "writer", "editor"].includes(role);
      case "Ready for Approval":
        return ["admin", "editor"].includes(role);
      case "Ready to Publish":
        return ["admin", "approver"].includes(role);
      default:
        return false;
    }
  };

  const statusOptions = [
    { value: "Pending", visible: true }, // Added Pending for un-started requests
    { value: "In Progress", visible: true },
    { value: "Ready for Review", visible: canSee("Ready for Review") },
    { value: "Ready for Approval", visible: canSee("Ready for Approval") },
    { value: "Ready to Publish", visible: canSee("Ready to Publish") },
    { value: "Edits Needed", visible: profile?.role !== "Writer" },
  ].filter((o) => o.visible);

  // ✅ NEW: Dual-Update function
  const handleStatusChange = async (newStatus) => {
    try {
      // 1. Update the Request
      await supabase
        .from("Requests")
        .update({ status: newStatus })
        .eq("id", requestId);

      // 2. Update the Article (if one has been linked)
      if (request.article_id) {
        await supabase
          .from("articles")
          .update({ progress: newStatus })
          .eq("id", request.article_id);
      }

      setRequest((prev) => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status.");
    }
  };

  return (
    <div style={styles.page}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          &larr; Back to Requests
        </button>

        <div style={{ display: "flex", gap: "12px" }}>
          {/* ✅ BUTTON 1: Open Existing (Greyed out if no article_id exists) */}
          <button
            disabled={!request.article_id}
            style={{
              ...styles.buttonBase,
              background: request.article_id ? "#f3f4f6" : "#e5e7eb",
              color: request.article_id ? "#111827" : "#9ca3af",
              cursor: request.article_id ? "pointer" : "not-allowed",
              border: "1px solid #d1d5db",
            }}
            onClick={() => {
              navigate(`/organization-portal/${companyId}/content-generation`, {
                state: { openArticle: { id: request.article_id } },
              });
            }}
          >
            📂 Open Existing Article
          </button>

          {/* ✅ BUTTON 2: Create Blank (Navigates WITHOUT autoOpenGenerate) */}
          <button
            style={{
              ...styles.buttonBase,
              background: "#f3f4f6",
              color: "#111827",
              border: "1px solid #d1d5db",
            }}
            onClick={() => {
              navigate(`/organization-portal/${companyId}/content-generation`, {
                state: {
                  openArticle: {
                    title: request.title,
                    project_id: request.project_id,
                    content_brief: data,
                    org_id: request.company_id,
                  },
                  linkedRequestId: request.id, // Tells TextBox to link upon save
                },
              });
            }}
          >
            📄 Create Blank Article
          </button>

          {/* ✅ BUTTON 3: Generate AI (Navigates WITH autoOpenGenerate) */}
          <button
            style={{
              ...styles.buttonBase,
              background: "#00876e",
              color: "white",
              border: "none",
            }}
            onClick={() => {
              navigate(`/organization-portal/${companyId}/content-generation`, {
                state: {
                  openArticle: {
                    title: request.title,
                    project_id: request.project_id,
                    content_brief: data,
                    org_id: request.company_id,
                  },
                  autoOpenGenerate: true,
                  linkedRequestId: request.id, // Tells TextBox to link upon save
                },
              });
            }}
          >
            ✨ Generate AI Article
          </button>
        </div>
      </div>

      <div style={styles.header}>
        <h1 style={styles.title}>{request.title || "Untitled Request"}</h1>
        <div style={styles.metaRow}>
          <span>
            <strong>Organization:</strong>{" "}
            {request.Organizations?.name || "N/A"}
          </span>
          <span>
            <strong>Project:</strong> {request.projects?.title || "N/A"}
          </span>
          <span>
            <strong>Date:</strong>{" "}
            {new Date(request.created_at).toLocaleDateString()}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <strong>Status:</strong>
            <select
              value={request.status || "Pending"}
              onChange={(e) => handleStatusChange(e.target.value)}
              style={{
                padding: "4px 10px",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                fontSize: "14px",
                background: "white",
                cursor: "pointer",
              }}
            >
              {statusOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.value}
                </option>
              ))}
            </select>
          </span>
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>General Information</h2>
        <div style={styles.grid}>
          <DetailItem label="Objective" value={data.objective} />
          <DetailItem label="Deadline" value={formatDeadline(data.deadline)} />
          <DetailItem label="Priority" value={data.priority} />
          <DetailItem label="Article Length" value={data.articleLength} />
          <DetailItem label="Purpose" value={data.purpose?.join(", ")} />
        </div>

        <hr style={styles.divider} />

        <h2 style={styles.sectionTitle}>Audience & Tone</h2>
        <div style={styles.grid}>
          <DetailItem label="Primary Audience" value={data.primaryAudience} />
          <DetailItem
            label="Secondary Audience"
            value={data.secondaryAudience}
          />
          <DetailItem
            label="Knowledge Level"
            value={data.audienceKnowledgeLevel}
          />
          <DetailItem label="Tone" value={data.tone?.join(", ")} />
        </div>

        <hr style={styles.divider} />

        <h2 style={styles.sectionTitle}>SEO & Formatting</h2>
        <div style={styles.grid}>
          <DetailItem
            label="Primary Keywords"
            value={data.primaryKeywordsRaw}
          />
          <DetailItem
            label="Secondary Keywords"
            value={data.secondaryKeywordsRaw}
          />
        </div>
        <DetailItem label="Call to Action (CTA)" value={data.cta} />
        <DetailItem label="Key Messages" value={data.keyMessagesRaw} />
        <DetailItem label="Reference URLs" value={data.referenceUrlsRaw} />
        <DetailItem
          label="Article Constraints"
          value={data.commonMistakesRaw}
        />

        <hr style={styles.divider} />

        <h2 style={styles.sectionTitle}>Additional Notes</h2>
        <p style={styles.text}>
          {data.additionalNotes || "No additional notes provided."}
        </p>

        <hr style={styles.divider} />

        <h2 style={styles.sectionTitle}>Attached Assets</h2>
        {assets.length > 0 ? (
          <ul style={styles.assetList}>
            {assets.map((asset, idx) => (
              <li key={idx} style={styles.assetItem}>
                <a
                  href={asset.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.link}
                >
                  📄 {asset.name}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p style={styles.text}>No assets were attached to this request.</p>
        )}
      </div>
    </div>
  );
}

// Helper component for layout
const DetailItem = ({ label, value }) => (
  <div style={styles.detailItem}>
    <strong style={styles.label}>{label}:</strong>
    <div style={styles.value}>{value || "—"}</div>
  </div>
);

const styles = {
  page: {
    padding: "28px",
    background: "#f6f7fb",
    minHeight: "100vh",
    color: "#111827",
  },
  backButton: {
    background: "none",
    border: "none",
    color: "#2f855a",
    fontWeight: "bold",
    cursor: "pointer",
    marginBottom: "16px",
    padding: 0,
  },
  header: { marginBottom: "24px" },
  title: { fontSize: 28, fontWeight: 800, margin: "0 0 12px 0" },
  metaRow: { display: "flex", gap: "24px", fontSize: 14, color: "#4b5563" },
  card: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    padding: "24px",
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    marginTop: 0,
    marginBottom: "16px",
    color: "#1f2937",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    marginBottom: "16px",
  },
  detailItem: { marginBottom: "12px" },
  label: {
    display: "block",
    fontSize: 12,
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: "4px",
  },
  value: { fontSize: 15, color: "#111827" },
  text: { fontSize: 15, color: "#111827", lineHeight: 1.5, marginTop: 0 },
  divider: { border: 0, borderTop: "1px solid #e5e7eb", margin: "24px 0" },
  assetList: { listStyleType: "none", padding: 0, margin: 0 },
  assetItem: {
    padding: "12px",
    background: "#f9fafb",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    marginBottom: "8px",
  },
  link: { color: "#2563eb", textDecoration: "none", fontWeight: "600" },
  generateButton: {
    background: "#00876e",
    color: "white",
    padding: "10px 16px",
    borderRadius: "6px",
    border: "none",
    fontWeight: "bold",
    cursor: "pointer",
    fontSize: "14px",
  },
  buttonBase: {
    padding: "10px 16px",
    borderRadius: "6px",
    fontWeight: "bold",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
};
